import React from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import { Input } from 'components/ui/input';
import { cn } from 'lib/utils';
import type { SourceFilter, StatusFilter } from './filterWords';

interface ChipOption<T extends string> {
  value: T;
  label: string;
}

function Chips<T extends string>({ label, options, value, onChange }: { label: string; options: ChipOption<T>[]; value: T; onChange: (next: T) => void }) {
  return (
    <div role="group" aria-label={label} className="no-scrollbar flex gap-2 overflow-x-auto">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={option.value === value}
          onClick={() => onChange(option.value)}
          className={cn(
            'h-10 shrink-0 whitespace-nowrap rounded-xl border px-3.5 text-[13px] font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:h-9',
            option.value === value
              ? 'border-foreground bg-foreground text-background'
              : 'border-border bg-card text-foreground/70 hover:bg-muted hover:text-foreground',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

interface WordsFiltersProps {
  query: string;
  onQuery: (next: string) => void;
  status: StatusFilter;
  onStatus: (next: StatusFilter) => void;
  source: SourceFilter;
  onSource: (next: SourceFilter) => void;
  total: number;
  learned: number;
}

/** Search plus the Hammasi / Yangi / O'rganilgan and Barcha testlar / IELTS / SAT chips of the board. */
export function WordsFilters({ query, onQuery, status, onStatus, source, onSource, total, learned }: WordsFiltersProps) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2 md:flex-row md:flex-wrap md:items-center md:justify-between md:gap-3">
      <div className="relative md:w-[340px]">
        <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
        <Input
          type="search"
          value={query}
          onChange={(event) => onQuery(event.target.value)}
          placeholder={t('vocab.search_placeholder')}
          aria-label={t('vocab.search_label')}
          className="h-[46px] rounded-xl pl-10 md:h-10"
        />
      </div>
      <div className="flex flex-col gap-2 md:flex-row md:gap-2.5">
        <Chips<StatusFilter>
          label={t('vocab.filter_status')}
          value={status}
          onChange={onStatus}
          options={[
            { value: 'all', label: t('vocab.filter_all') },
            { value: 'new', label: t('vocab.filter_new', { count: total - learned }) },
            { value: 'learned', label: t('vocab.filter_learned', { count: learned }) },
          ]}
        />
        <Chips<SourceFilter>
          label={t('vocab.filter_source')}
          value={source}
          onChange={onSource}
          options={[
            { value: 'all', label: t('vocab.filter_all_tests') },
            { value: 'IELTS', label: 'IELTS' },
            { value: 'SAT', label: 'SAT' },
          ]}
        />
      </div>
    </div>
  );
}
